import { FieldValidator } from "formik";
import { useCallback } from "react";
import { sprintf } from "sprintf-js";
import useTranslations from "../../hooks/useTranslations";
import { Translations } from "../../localization/Translations";
import { isNullOrWhiteSpace } from "../../shared/string";

/* eslint-disable @typescript-eslint/no-explicit-any */

export type Validator = {
  _type: string;
  value?: any;
  message?: string;
};

export type FileWithValidationInfo = {
  file?: File;
  customErrors?: string;
};

type ValidatorFunctionProps = {
  fieldValue: any;
  translations: Translations;
  customMessage?: string;
  compareValue?: any;
};

type ValidatorFunction = (
  props: ValidatorFunctionProps
) => string | void | Promise<string | void>;
export type CustomValidatorFunction = (
  fieldValue: any,
  translations: Translations
) => string | void | Promise<string | void>;

const commonValidators: {
  [key: string]: ValidatorFunction;
} = {
  required: ({ fieldValue, customMessage, translations }) => {
    if (
      !fieldValue ||
      (typeof fieldValue === "string" && isNullOrWhiteSpace(fieldValue)) ||
      (Array.isArray(fieldValue) && fieldValue.length === 0)
    ) {
      return !isNullOrWhiteSpace(customMessage)
        ? customMessage
        : translations.required ?? "";
    }
  },
  maxFileSize: ({ compareValue, fieldValue, customMessage, translations }) => {
    const fileWithValidationInfo = fieldValue as FileWithValidationInfo;

    if (fileWithValidationInfo) {
      const { file, customErrors } = fileWithValidationInfo;

      if (!isNullOrWhiteSpace(customErrors)) {
        return fileWithValidationInfo.customErrors;
      } else if (file) {
        const fileSize = file.size / 1024 / 1024;

        if (fileSize > compareValue) {
          return !isNullOrWhiteSpace(customMessage)
            ? customMessage
            : translations.maxFileSize ?? "";
        }
      }
    }
  },
  maxLength: ({ compareValue, fieldValue, customMessage, translations }) => {
    if (typeof fieldValue === "string" && fieldValue.trim().length > compareValue) {
      return !isNullOrWhiteSpace(customMessage)
        ? customMessage
        : translations.maxLength ?? "";
    }
  },
  minLength: ({ compareValue, fieldValue, customMessage, translations }) => {
    if (typeof fieldValue === "string" && fieldValue.trim().length < compareValue) {
      return !isNullOrWhiteSpace(customMessage)
        ? customMessage
        : translations.minLength ?? "";
    }
  },
  maxValue: ({ compareValue, fieldValue, customMessage, translations }) => {
    const isNumber =
      typeof fieldValue === "number" ||
      (typeof fieldValue === "string" && !isNullOrWhiteSpace(fieldValue));
    const numberValue = Number(fieldValue);
    if (isNumber && numberValue > compareValue) {
      return !isNullOrWhiteSpace(customMessage)
        ? customMessage
        : translations.maxValue ?? "";
    }
  },
  minValue: ({ compareValue, fieldValue, customMessage, translations }) => {
    const isNumber =
      typeof fieldValue === "number" ||
      (typeof fieldValue === "string" && !isNullOrWhiteSpace(fieldValue));
    const numberValue = Number(fieldValue);
    if (isNumber && numberValue < compareValue) {
      return !isNullOrWhiteSpace(customMessage)
        ? customMessage
        : translations.minValue ?? "";
    }
  }
};

export const useFieldValidator = (
  validators: Validator[] | undefined,
  customValidator?: CustomValidatorFunction
): FieldValidator => {
  const translations = useTranslations();

  return useCallback(
    async value => {
      if (validators?.length) {
        let message: string | void;

        for (const validator of validators) {
          message = await commonValidators[validator._type]?.({
            compareValue: validator.value,
            fieldValue: value,
            customMessage: validator.message,
            translations
          });

          if (message) {
            return sprintf(message, validator.value);
          }
        }
      }

      if (customValidator) {
        return await customValidator(value, translations);
      }
    },
    [customValidator, translations, validators]
  );
};
